// ── Reading a finger on the page: is it turning it, and does the turn land? ──
// Pure functions, so the rules can be tested without a touch screen. The hook
// in usePageTurn.js feeds them pointer positions and acts on the answers.

// A touch this close to either side of the screen is the browser's own back /
// forward gesture (iOS Safari, Android), never a page turn.
export const EDGE_PX = 28;
// How far the finger travels before we decide what kind of drag it is.
export const SLOP_PX = 10;
// Sideways must beat vertical by this much to count as a turn (about 40°).
export const HORIZONTAL_RATIO = 1.2;
// Release past this share of the page width and the turn completes …
export const DISTANCE_SHARE = 0.35;
// … or flick faster than this (px per ms) in the direction of the drag.
export const FLICK_VELOCITY = 0.45;
// A flick still has to travel a little, so a twitch is not a turn.
export const FLICK_MIN_PX = 24;

// Whether a touch starting at clientX belongs to the browser's edge gesture.
export function edgeRejected(x, viewportWidth, edge = EDGE_PX) {
  return x < edge || x > viewportWidth - edge;
}

// 'pending' until the finger has moved far enough to tell, then 'horizontal'
// (a page turn) or 'vertical' (someone scrolling the text).
export function classifyDrag(dx, dy, slop = SLOP_PX, ratio = HORIZONTAL_RATIO) {
  const ax = Math.abs(dx), ay = Math.abs(dy);
  if (ax < slop && ay < slop) return 'pending';
  return ax > ay * ratio ? 'horizontal' : 'vertical';
}

// Dragging towards a side that has no page (Revelation 22 → next) gives way
// a little and then stiffens, like paper held at the spine.
export function rubberBand(dx, width) {
  const limit = Math.max(40, width * 0.12);
  const sign = dx < 0 ? -1 : 1;
  return sign * limit * (1 - 1 / (Math.abs(dx) / limit * 0.55 + 1));
}

// What a released drag does: 'next', 'prev' or 'cancel' (spring back).
// dx < 0 means the page was pulled to the left, towards the next page.
// vx is the finger's velocity at release in px/ms (negative = leftwards).
export function releaseDecision({ dx, vx = 0, width, canPrev, canNext, share = DISTANCE_SHARE }) {
  const dir = dx < 0 ? 1 : dx > 0 ? -1 : 0;
  if (!dir) return 'cancel';
  if (dir > 0 && !canNext) return 'cancel';
  if (dir < 0 && !canPrev) return 'cancel';
  const far = Math.abs(dx) >= width * share;
  // A flick counts only when it is moving the same way the page was pulled;
  // a drag that swings back towards the start springs back.
  const flick = Math.abs(dx) >= FLICK_MIN_PX && Math.abs(vx) >= FLICK_VELOCITY && Math.sign(vx) === Math.sign(dx);
  const reversing = Math.abs(vx) >= FLICK_VELOCITY && Math.sign(vx) === -Math.sign(dx);
  if (flick || (far && !reversing)) return dir > 0 ? 'next' : 'prev';
  return 'cancel';
}

// How long the page takes to travel the rest of the way, in ms. A fast flick
// carries its speed into the settle; a slow release glides.
export function settleDuration(remainingPx, width, vx = 0, base = 300) {
  const share = Math.min(1, Math.abs(remainingPx) / Math.max(1, width));
  let ms = base * share;
  if (Math.abs(vx) > 0.1) ms = Math.min(ms, (Math.abs(remainingPx) / Math.abs(vx)) * 1.2);
  return Math.round(Math.max(120, Math.min(base, ms)));
}

// Velocity from the last ~100 ms of movement, which is what the finger was
// doing as it let go (a whole-gesture average would hide a final flick).
export class VelocityTracker {
  constructor(windowMs = 100) { this.windowMs = windowMs; this.samples = []; }
  add(x, t) {
    this.samples.push({ x, t });
    while (this.samples.length > 2 && t - this.samples[0].t > this.windowMs) this.samples.shift();
  }
  velocity() {
    const s = this.samples;
    if (s.length < 2) return 0;
    const a = s[0], b = s[s.length - 1];
    const dt = b.t - a.t;
    return dt > 0 ? (b.x - a.x) / dt : 0;
  }
}
